/**
 * Pages/Panel/ReportesNomina/ReporteAcumulados.js
 *
 * Acumulados del ejercicio por empleado: un renglón por empleado y una columna por
 * Tipo de Acumulado, con corte opcional por rango de meses.
 *
 * La fuente es la colección `Acumulado`, que es el almacén oficial del sistema — la
 * misma que lee el modal de acumulados de un empleado. NO se derivan de las líneas
 * de prenómina: `ConceptoNomina` no tiene banderas de ISR ni de IMSS, así que
 * reconstruir esos buckets desde los conceptos sería inventar una regla que el resto
 * del sistema no usa.
 *
 * `TipoAcumulado.tipoAcumulado` distingue importes (1) de días y horas (2), que es
 * lo que decide el formato de cada columna en el Excel.
 */

import { message, PageHeader, Select, Space, Spin, Table, Tabs, Tag } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  SelectItem,
  ButtonItem,
  ErrorItem,
  dineroDisabledMask,
} from "../../../Components/Items";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
import {
  headerStyle,
  textoStyle,
  centroStyle,
  numeroStyle,
  monedaStyle,
  totalTextoStyle,
  totalNumeroStyle,
  totalMonedaStyle,
  celda,
  celdaNumero,
  celdaMoneda,
} from "./estilosExcelNomina";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Ejercicios que se ofrecen: del actual hacia atrás. Cinco años cubre cualquier
// revisión y evita un select interminable.
const EJERCICIOS = Array.from({ length: 6 }, (_, i) => moment().year() - i);

// tipoAcumulado 2 = días y horas: no llevan formato de moneda.
const esDiasHoras = (columna) => columna.tipoAcumulado === 2;

class ReporteAcumulados extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    consultado: false,
    empresa: null,
    anio: moment().year(),
    columnas: [],
    filas: [],
    objetoRegistrosPatronales: null,
  };

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  componentDidMount() {
    axios
      .post(
        "registrosPatronales/selectRegistrosPatronalesItem",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            objetoRegistrosPatronales: (res.data.registrosPatronales || []).map((value) => (
              <Option value={value._id} key={value._id}>
                {value.registroPatronalIMSS}
              </Option>
            )),
          });
        }
      })
      .catch(() => {});
  }

  consultarReporte(values) {
    this.setState({ loading: true });

    axios
      .post(
        "reportesNomina/reporteAcumulados",
        {
          empresaId: this.props.empresaId,
          anio: values.anio,
          mesInicio: values.mesInicio || null,
          mesFin: values.mesFin || null,
          registrosPatronales: values.registrosPatronales || [],
          departamentos: values.departamentos || [],
          empleados: values.empleados || [],
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            loading: false,
            consultado: true,
            empresa: res.data.empresa,
            anio: res.data.anio,
            columnas: res.data.columnas || [],
            filas: res.data.filas || [],
          });
        } else if (res.data.success === 1) {
          message.warning(res.data.message, 8);
          this.setState({ loading: false, consultado: true, columnas: [], filas: [] });
        } else {
          message.error("Ha sucedido un error y no se cargó el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Ha sucedido un error y no se cargó el reporte");
        this.setState({ loading: false });
      });
  }

  descargarExcel() {
    const { columnas, filas, empresa, anio } = this.state;
    if (filas.length === 0) return;

    const aoa = [];
    aoa.push([celda(empresa?.nombre || "", { font: { bold: true, sz: 14 } })]);
    aoa.push([celda(`Acumulados del ejercicio ${anio}`, { font: { sz: 11 } })]);
    aoa.push([]);

    aoa.push([
      celda("Código", headerStyle),
      celda("Empleado", headerStyle),
      celda("RFC", headerStyle),
      celda("NSS", headerStyle),
      celda("Departamento", headerStyle),
      celda("Reg. Pat.", headerStyle),
      celda("Estatus", headerStyle),
      ...columnas.map((c) => celda(c.nombre, headerStyle)),
    ]);

    const totales = columnas.map(() => 0);

    filas.forEach((fila) => {
      const renglon = [
        celda(fila.codigo == null ? "" : String(fila.codigo).padStart(4, "0"), textoStyle),
        celda(fila.nombre, textoStyle),
        celda(fila.rfc, centroStyle),
        celda(fila.nss, centroStyle),
        celda(fila.departamento, textoStyle),
        celda(fila.registroPatronalIMSS, centroStyle),
        celda(fila.baja ? "Baja" : "Activo", centroStyle),
      ];
      columnas.forEach((col, i) => {
        const valor = fila.totales ? fila.totales[col._id] : undefined;
        const numero = valor === undefined ? "" : valor;
        renglon.push(
          esDiasHoras(col)
            ? celdaNumero(numero, numeroStyle)
            : celdaMoneda(numero, monedaStyle)
        );
        if (typeof valor === "number") totales[i] += valor;
      });
      aoa.push(renglon);
    });

    aoa.push([
      celda("TOTAL", totalTextoStyle),
      ...Array.from({ length: 6 }, () => celda("", totalTextoStyle)),
      ...columnas.map((col, i) =>
        esDiasHoras(col)
          ? celdaNumero(totales[i], totalNumeroStyle)
          : celdaMoneda(totales[i], totalMonedaStyle)
      ),
    ]);

    const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
    hoja["!cols"] = [
      { wch: 10 }, { wch: 34 }, { wch: 15 }, { wch: 14 },
      { wch: 22 }, { wch: 16 }, { wch: 10 },
      ...columnas.map(() => ({ wch: 16 })),
    ];

    const libro = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(libro, hoja, "Acumulados");
    XLSXStyle.writeFile(libro, `Acumulados_${anio}.xlsx`);
  }

  render() {
    const { loading, consultado, columnas, filas, anio } = this.state;

    const columnasTabla = [
      {
        title: "Código",
        dataIndex: "codigo",
        width: 90,
        fixed: "left",
        render: (v) => (v == null ? "—" : String(v).padStart(4, "0")),
      },
      {
        title: "Empleado",
        dataIndex: "nombre",
        width: 240,
        fixed: "left",
        render: (v, fila) => (
          <span>
            {v}
            {fila.baja && (
              <Tag color="red" style={{ marginLeft: 6 }}>
                Baja
              </Tag>
            )}
          </span>
        ),
      },
      { title: "Departamento", dataIndex: "departamento", width: 160 },
      ...columnas.map((col) => ({
        title: col.nombre,
        key: col._id,
        width: 150,
        align: "right",
        render: (_, fila) => {
          const valor = fila.totales ? fila.totales[col._id] : undefined;
          if (valor === undefined) return <span style={{ color: "#bbb" }}>—</span>;
          return esDiasHoras(col) ? valor : dineroDisabledMask(valor);
        },
      })),
    ];

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteAcumulados"
          onFinish={this.consultarReporte.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{ anio: moment().year() }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Acumulados por Empleado"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <div style={{ display: "flex", justifyContent: "right" }}>
                <Space>
                  {filas.length > 0 && (
                    <ButtonItem
                      blanco
                      onClick={() => this.descargarExcel()}
                      icon={<FileExcelOutlined />}
                      style={{ display: "block", margin: "0 0 0 auto" }}
                    >
                      Descargar Excel
                    </ButtonItem>
                  )}
                  <ButtonItem
                    type="primary"
                    htmlType="submit"
                    style={{ display: "block", margin: "0 0 0 auto" }}
                  >
                    Consultar
                  </ButtonItem>
                </Space>
              </div>
            }
          />
          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                marginBottom: 10,
              }}
            >
              {/* forceRender en todas: sin él se pierden los campos de las
                  pestañas que el usuario no llegó a abrir. */}
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Ejercicio">
                  <Space wrap>
                    <FormItem name="anio" required margin label="Año">
                      <SelectItem width={120} placeholder="Ejercicio">
                        {EJERCICIOS.map((value) => (
                          <Option value={value} key={value}>
                            {value}
                          </Option>
                        ))}
                      </SelectItem>
                    </FormItem>
                    <FormItem name="mesInicio" margin label="Desde el mes">
                      <SelectItem width={140} allowClear placeholder="Enero">
                        {MESES.map((nombre, i) => (
                          <Option value={i + 1} key={nombre}>
                            {nombre}
                          </Option>
                        ))}
                      </SelectItem>
                    </FormItem>
                    <FormItem name="mesFin" margin label="Hasta el mes">
                      <SelectItem width={140} allowClear placeholder="Diciembre">
                        {MESES.map((nombre, i) => (
                          <Option value={i + 1} key={nombre}>
                            {nombre}
                          </Option>
                        ))}
                      </SelectItem>
                    </FormItem>
                  </Space>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Registro Patronal">
                  <FormItem name="registrosPatronales" noStyle margin>
                    <SelectItem
                      width="90%"
                      mode="multiple"
                      placeholder="Todos los registros patronales"
                    >
                      {this.state.objetoRegistrosPatronales}
                    </SelectItem>
                  </FormItem>
                </TabPane>
                <TabPane tabKey="3" key="3" forceRender tab="Departamentos">
                  <FormItem name="departamentos" noStyle margin>
                    <SelectDepartamentosItem
                      mode="multiple"
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los departamentos"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
                <TabPane tabKey="4" key="4" forceRender tab="Empleados">
                  <FormItem name="empleados" noStyle margin>
                    <SelectEmpleadosItem
                      mode="multiple"
                      incluirBajas
                      empresaId={this.props.empresaId}
                      headersToken={this.props.headersToken}
                      placeholder="Todos los empleados"
                      style={{ width: "90%" }}
                    />
                  </FormItem>
                </TabPane>
              </Tabs>
            </div>

            {consultado && (
              <div
                style={{
                  backgroundColor: "white",
                  padding: 20,
                  paddingTop: 10,
                  marginBottom: 15,
                  borderRadius: 5,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                }}
              >
                <p style={{ marginBottom: 10, color: "#666" }}>
                  {filas.length} empleado(s) con acumulados en {anio} ·{" "}
                  {columnas.length} tipo(s) de acumulado.
                </p>
                <Table
                  size="small"
                  rowKey="empleadoId"
                  dataSource={filas}
                  columns={columnasTabla}
                  pagination={{ defaultPageSize: 20 }}
                  scroll={{ x: 500 + columnas.length * 150 }}
                />
              </div>
            )}
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(ReporteAcumulados);
